import axios from 'axios'
import { authStorage } from '../../utils/authStorage'
import { sweetAlert } from '../sweetAlertService'
import { API_BASE_URL } from '../../config/api'

// Crear instancia de axios con configuración centralizada
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor para agregar el token a cada petición
apiClient.interceptors.request.use(
  (config) => {
    const token = authStorage.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Log de la petición en desarrollo (solo si no está marcado como silent)
    // if (import.meta.env.DEV && !config._silent) {
    //   console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`, config.data || '')
    // }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => {
    // Log de la respuesta en desarrollo (solo si no está marcado como silent)
    // if (import.meta.env.DEV && !response.config._silent) {
    //   console.log(`📥 ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data)
    // }

    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // Si el error es 401 y no es el login
    if (error.response?.status === 401 && !originalRequest.url.includes('/auth/login')) {
      // Token expirado o inválido
      authStorage.clearAll()
      
      // Mostrar mensaje y redirigir al login
      await sweetAlert.showError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.')
      window.location.href = '/login'
      
      return Promise.reject(error)
    }
    
    // Si el error es 403 - Sin permisos
    if (error.response?.status === 403) {
      sweetAlert.showError('No tienes permisos para realizar esta acción')
      return Promise.reject(error)
    }
    
    // Si el error es 404 - No encontrado
    if (error.response?.status === 404) {
      // Endpoints que esperan 404 como respuesta válida (casos de negocio)
      const expectedNotFoundEndpoints = [
        '/unidades_medicas/conductor/', // Usuario sin unidad asignada
      ]

      const isExpectedNotFound = expectedNotFoundEndpoints.some(endpoint =>
        error.config.url.includes(endpoint)
      )

      if (!isExpectedNotFound) {
        console.error(`🔴 Recurso no encontrado: ${error.config.url}`)
      } else if (import.meta.env.DEV) {
        console.log(`ℹ️ Recurso no disponible: ${error.config.url}`)
      }
    }

    // Si el error es 500 - Error del servidor
    if (error.response?.status === 500) {
      sweetAlert.showError('Error en el servidor. Por favor, intenta más tarde.')
    }

    // Log del error en desarrollo (solo si no es un 404 esperado)
    if (import.meta.env.DEV && error.response?.status !== 404) {
      console.error(`🔴 Error en ${error.config?.method?.toUpperCase()} ${error.config?.url}:`, error.response?.data || error.message)
    }
    
    return Promise.reject(error)
  }
)

// Funciones helper para los métodos HTTP
const api = {
  // GET
  get: (url, config = {}) => apiClient.get(url, config),
  
  // POST
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  
  // PUT
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),
  
  // PATCH
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),
  
  // DELETE
  delete: (url, config = {}) => apiClient.delete(url, config),
  
  // Upload de archivos
  upload: (url, formData, onUploadProgress) => {
    return apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    })
  }
}

export { apiClient, API_BASE_URL }
export default api